/** *+ REQUEST ****/

export type GroupByOptions =
    | 'layer:1'
    | 'layer:2'
    | 'layer:3'
    | 'note'
    | 'user'
    | 'group'
    | 'day'
    | 'week'
    | 'month'
    | 'year';
const VALID_GROUP_OPTIONS: GroupByOptions[] = [
    'layer:1',
    'layer:2',
    'layer:3',
    'note',
    'user',
    'group',
    'day',
    'week',
    'month',
    'year',
];
export function validateGroupOption(groupOptions: GroupByOptions[]): boolean {
    return groupOptions.every((el) => VALID_GROUP_OPTIONS.includes(el));
}

export type UserFormat = 'full_name' | 'email' | 'full_name_and_email' | 'id' | 'full_name_and_id' | 'default';

export interface RoundingConfig {
    unit: number; // Rounding unit in minutes (should be limited to 1-60 range)
    type: 'up' | 'down' | 'nearest';
}

export interface ReportFilterForm {
    note?: string;
    hasNote?: boolean;
    startDate: string; // ISO-8601 without timezone, e.g. 2025-03-01T00:00:00.000
    endDate: string; // ISO-8601 without timezone, e.g. 2025-03-31T23:59:59.999
    strictDate?: boolean;
    userIds?: number[];
    tileIds?: number[];
    billed?: boolean;
    groupIds?: number[];
    activityIds?: number[];
}

export class ReportFormatForm {
    user: UserFormat = 'default';
    // includes activity notes in a concatenated string
    includeNotes?: boolean;
    /** Rounding configuration for durations. If omitted, no rounding is applied. */
    rounding?: RoundingConfig;
    /**
     * @deprecated Use rounding object instead.
     * applies rounding to duration values in the report iff enabled in the users report settings
     */
    applyRounding?: boolean;
    includeActivityIds?: boolean; // if true, the report response will include and use activity IDs for each grouping
}

export interface ReportSpecForm {
    utcOffset: string;
    filter: ReportFilterForm;
    groupBy: GroupByOptions[];
    format: ReportFormatForm;
}

export interface ReportRequestForm {
    reports: ReportSpecForm[];
}

/** *+ RESPONSE ****/

export interface ReportResponseForm {
    reports: ReportResponse[];
}

// Attention: index of number field must be in sync with RowIndex enum below
export type ReportRow = [number, ...string[]];

export interface ReportResponse {
    headers: string[];
    types: string[]; // note: This is currently not used in the frontend, but may be used later
    rows: ReportRow[];
    totalSeconds: number;
}

export enum RowIndex {
    SECONDS = 0,
    COLOR = 1,
    GROUP1 = 2,
    GROUP2 = 3,
}

/**
 * This method will transform the ReportResponse object to a two-dimensional string array
 * with headers as the first row (index 0) and all data rows as rows (index) 1 to n.
 * It is a table representation of the report result.
 * @param report The report response to be transformed.
 * @returns A two-dimensional string array (table) representing this response.
 */
export function responseToStringArray(report: ReportResponse): string[][] {
    const rows: ReportRow[] = report.rows;
    const stringRows: string[][] = rows.map(([first, ...rest]) => [first.toString(), ...rest]);
    return [report.headers, ...stringRows];
}
// TODO: find a good place where to put model transformation functions
